// File: js/db/receipt-blocks.js
//
// Receipt-number supply for this device (invariant 3; audit C1).
//
// The pre-rewrite system minted `TYPE-YYYYMMDD-` + `Math.floor(Math.random()*10000)`
// — 10,000 slots drawn randomly per prefix per day, a 39% chance of a collision at
// 100 receipts/day. The collision then violated `UNIQUE`, the client misread it as
// "we must be offline", and the sale was parked under a status nothing ever read.
//
// Here a number is ONLY ever issued from a pre-allocated sequential range this
// device can prove it owns from its own LOCAL state. Never random. Never resumed
// from fresh state. Never reused. When it cannot issue, the desk says « Plus de
// numéros de reçu — utilisez le carnet papier. » (spec §9).
//
// Local state lives in `meta.block = { start, end, next }`:
//   • `next` is the number that will be issued next, consumed INSIDE the write
//     transaction so it is spent exactly when an event uses it.
//   • remaining = end − next + 1. ≤ 100 → low watermark: top up when online.
//   • next > end → exhausted: no more numbers offline.
//   • NO block key at all (fresh install, wiped storage, cleared site data) →
//     NEVER adopt a prior range. Request a brand-new block with reason
//     `'meta_loss'`. A partially-consumed block resumed from fresh state re-issues
//     numbers already printed on paper in patients' hands — the one failure this
//     whole subsystem exists to prevent (harness F10).

import { getMeta, setMeta, META_KEYS } from './schema.js';
import { CollectionRefusedError } from './errors.js';

/** Request a top-up once this many numbers (or fewer) remain. */
export const LOW_WATERMARK = 100;

/**
 * @typedef {Object} BlockState
 * @property {boolean} hasBlock False when meta carries no block (fresh or wiped).
 * @property {number} [start]
 * @property {number} [end]
 * @property {number} [next] The number that would be issued next.
 * @property {number} remaining Numbers left; 0 when none or exhausted.
 * @property {boolean} exhausted No number can be issued right now.
 * @property {boolean} low At or under the low watermark.
 * @property {boolean} needsBlock A new block should be requested when online.
 * @property {'meta_loss'|'low_watermark'} requestReason Why.
 */

/**
 * Inspect this device's local block state. Pure read — allocates nothing.
 *
 * @param {import('dexie').Dexie} db
 * @returns {Promise<BlockState>}
 */
export async function readBlockState(db) {
    const block = await getMeta(db, META_KEYS.BLOCK, null);

    if (!isWellFormedBlock(block)) {
        // Fresh or wiped meta — and a malformed block is treated identically,
        // because "I cannot prove what I own" and "I own nothing" have exactly the
        // same correct answer: issue nothing, ask for a brand-new range.
        return {
            hasBlock: false,
            remaining: 0,
            exhausted: true,
            low: true,
            needsBlock: true,
            requestReason: 'meta_loss'
        };
    }

    const remaining = block.end - block.next + 1;
    const exhausted = remaining <= 0;
    return {
        hasBlock: true,
        start: block.start,
        end: block.end,
        next: block.next,
        remaining: Math.max(0, remaining),
        exhausted,
        low: remaining <= LOW_WATERMARK,
        needsBlock: exhausted || remaining <= LOW_WATERMARK,
        requestReason: 'low_watermark'
    };
}

/**
 * Consume the next receipt number, advancing `next`.
 *
 * MUST be called inside the same Dexie transaction as the ledger and outbox
 * writes, so the number is spent atomically with the event that uses it: if the
 * write rolls back, the number is un-spent and no gap appears.
 *
 * Re-checks availability HERE even though the caller already passed
 * `collectionAllowed()`. That second check is the whole point — it closes the
 * TOCTOU window between the gate and the write, during which a second tab could
 * have drained the last number.
 *
 * @param {import('dexie').Dexie} db Called within `db.transaction('rw', …)`.
 * @returns {Promise<number>} The issued receipt number.
 * @throws {CollectionRefusedError} `'no-receipt-numbers'` when none remain.
 */
export async function takeReceiptNumber(db) {
    const block = await getMeta(db, META_KEYS.BLOCK, null);
    if (!isWellFormedBlock(block) || block.next > block.end) {
        throw new CollectionRefusedError('no-receipt-numbers');
    }
    const number = block.next;
    await setMeta(db, META_KEYS.BLOCK, { start: block.start, end: block.end, next: number + 1 });
    return number;
}

/**
 * Adopt a freshly allocated block as this device's current range, issuing from its
 * first number.
 *
 * Any unused tail of the previous block is deliberately ABANDONED. It becomes an
 * unused tail of a live or retired server block, which `v_receipt_gaps` does not
 * flag (spec §10: "Unused tails of live blocks are not gaps"). It is never resumed
 * and never re-issued.
 *
 * @param {import('dexie').Dexie} db
 * @param {{block_start:number, block_end:number}} block From `allocate_receipt_block`.
 * @returns {Promise<{start:number, end:number, next:number}>}
 * @throws {CollectionRefusedError} When the server hands back an unusable range.
 */
export async function applyBlockArrival(db, block) {
    const start = block && block.block_start;
    const end = block && block.block_end;
    if (!Number.isInteger(start) || !Number.isInteger(end) || end < start) {
        // Refuse rather than adopt a range we cannot reason about. A device with no
        // block falls back to the paper book; a device with a nonsense block would
        // issue nonsense numbers.
        throw new CollectionRefusedError('no-receipt-numbers');
    }
    const adopted = { start, end, next: start };
    await setMeta(db, META_KEYS.BLOCK, adopted);
    return adopted;
}

/**
 * Request a new block from the server when local state needs one, then adopt it.
 *
 * Fresh or wiped meta asks with reason `'meta_loss'`, which retires this device's
 * prior blocks server-side; a live block at or under the watermark asks with
 * `'low_watermark'` (a top-up). No-op when no block is needed.
 *
 * Note the client never sends `'initial'`, even on a brand-new device: locally,
 * "never had a block" and "had one and lost it" are indistinguishable, so the only
 * safe reading of an absent block is the pessimistic one. `'meta_loss'` on a device
 * with no prior blocks retires nothing and costs nothing.
 *
 * The transport is injected; a transport failure propagates so the sync engine can
 * back off. Meanwhile the device simply keeps refusing collection — which is the
 * correct behaviour, not a degraded one.
 *
 * @param {import('dexie').Dexie} db
 * @param {{allocateReceiptBlock: (q:{deviceId:string, reason:string, size?:number}) => Promise<{block_start:number, block_end:number}>}} transport
 * @param {string} deviceId
 * @returns {Promise<{requested:boolean, reason?:string, block?:object}>}
 */
export async function maybeRequestBlock(db, transport, deviceId) {
    // An unprovisioned device has nothing to allocate a block AGAINST: the server
    // raises `unknown or inactive device`, which the sync engine would swallow and
    // retry forever. Refuse locally and say why — the device is already refusing
    // collection for the same reason (spec §7's identity gate).
    if (deviceId == null || deviceId === '') {
        return { requested: false, reason: 'device-not-provisioned' };
    }
    const state = await readBlockState(db);
    if (!state.needsBlock) return { requested: false };
    const block = await transport.allocateReceiptBlock({ deviceId, reason: state.requestReason });
    await applyBlockArrival(db, block);
    return { requested: true, reason: state.requestReason, block };
}

/**
 * A block is usable only if every field is an integer and the range is sane.
 * @param {*} block
 * @returns {boolean}
 */
function isWellFormedBlock(block) {
    return !!block
        && Number.isInteger(block.start)
        && Number.isInteger(block.end)
        && Number.isInteger(block.next)
        && block.end >= block.start
        && block.next >= block.start;
}
